import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Table, Alert, Spinner, Card } from "react-bootstrap";
import PrettyContainer from "../../prettyContainer/PrettyContainer";
import { getAllDomains, createDomain, updateDomain, deleteDomain, uploadImage } from "../../../helpers/api";

export default function DomainManagementPage() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    url: "",
    img: "",
    description: "",
    enabled: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = () => {
    setLoading(true);
    getAllDomains()
      .then((data) => {
        setDomains(data.domains || []);
        setError("");
      })
      .catch((err) => {
        setError("Error loading domains: " + err);
        setTimeout(() => setError(""), 5000);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleShowModal = (domain = null) => {
    if (domain) {
      setEditingDomain(domain);
      setFormData({
        name: domain.name,
        url: domain.url,
        img: domain.img,
        description: domain.description,
        enabled: domain.enabled,
      });
      setImagePreview(domain.img ? `${process.env.REACT_APP_API.replace('/api', '')}${domain.img}` : "");
    } else {
      setEditingDomain(null);
      setFormData({
        name: "",
        url: "",
        img: "",
        description: "",
        enabled: true,
      });
      setImagePreview("");
    }
    setImageFile(null);
    setShowModal(true);
    setError("");
    setSuccess("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDomain(null);
    setImageFile(null);
    setImagePreview("");
    setError("");
    setSuccess("");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.name || !formData.url) {
      setError("Name and URL are required");
      return;
    }

    try {
      setLoading(true);

      let imageUrl = formData.img;

      // Upload image if a new one was selected
      if (imageFile) {
        setUploading(true);
        const uploadResult = await uploadImage(imageFile);
        imageUrl = uploadResult.imageUrl;
        setUploading(false);
      }

      const domainData = {
        ...formData,
        img: imageUrl,
      };

      if (editingDomain) {
        await updateDomain(editingDomain._id, domainData);
      } else {
        await createDomain(domainData);
      }

      loadDomains();
      handleCloseModal();
      
      // Show success message after modal is closed
      const successMsg = editingDomain ? "Domain updated successfully" : "Domain created successfully";
      setSuccess(successMsg);
      setTimeout(() => setSuccess(""), 3000);
      
    } catch (err) {
      setError("Error saving domain: " + err);
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (domain) => {
    if (window.confirm(`Are you sure you want to delete "${domain.name}"?`)) {
      setLoading(true);
      deleteDomain(domain._id)
        .then(() => {
          setSuccess("Domain deleted successfully");
          loadDomains();
          setTimeout(() => setSuccess(""), 3000);
        })
        .catch((err) => {
          setError("Error deleting domain: " + err);
          setTimeout(() => setError(""), 5000);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <PrettyContainer tittle="DOMAIN MANAGEMENT">
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="mb-3">
        <Button variant="primary" onClick={() => handleShowModal()}>
          Add New Domain
        </Button>
      </div>

      {loading && !showModal ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th style={{ width: "80px" }}>Image</th>
              <th style={{ width: "150px" }}>Name</th>
              <th style={{ width: "200px" }}>URL</th>
              <th style={{ width: "auto", maxWidth: "300px" }}>Description</th>
              <th style={{ width: "100px" }}>Status</th>
              <th style={{ width: "180px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {domains.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  No domains found. Add your first domain!
                </td>
              </tr>
            ) : (
              domains.map((domain) => (
                <tr key={domain._id}>
                  <td style={{ width: "80px" }}>
                    {domain.img ? (
                      <img
                        src={`${process.env.REACT_APP_API.replace('/api', '')}${domain.img}`}
                        alt={domain.name}
                        style={{ width: "50px", height: "50px", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          backgroundColor: "#e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No Image
                      </div>
                    )}
                  </td>
                  <td style={{ width: "150px", wordBreak: "break-word" }}>{domain.name}</td>
                  <td style={{ width: "200px", wordBreak: "break-all" }}>
                    <a href={domain.url} target="_blank" rel="noopener noreferrer">
                      {domain.url.length > 50 ? domain.url.substring(0, 50) + "..." : domain.url}
                    </a>
                  </td>
                  <td style={{ 
                    maxWidth: "300px", 
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                    {domain.description}
                  </td>
                  <td style={{ width: "100px", textAlign: "center" }}>
                    <span className={`badge ${domain.enabled ? "bg-success" : "bg-secondary"}`}>
                      {domain.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td style={{ width: "180px", whiteSpace: "nowrap" }}>
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowModal(domain)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(domain)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingDomain ? "Edit Domain" : "Add New Domain"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter domain name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>URL *</Form.Label>
              <Form.Control
                type="url"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://example.com"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter domain description"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              <Form.Text className="text-muted">
                Upload an image (JPEG, PNG, GIF, SVG, WebP - Max 5MB)
              </Form.Text>
              {imagePreview && (
                <Card className="mt-3">
                  <Card.Body>
                    <Card.Title>Preview:</Card.Title>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ maxWidth: "200px", maxHeight: "200px" }}
                    />
                  </Card.Body>
                </Card>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="enabled"
                label="Enabled"
                checked={formData.enabled}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading || uploading}>
              {loading || uploading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {uploading ? "Uploading..." : "Saving..."}
                </>
              ) : (
                "Save"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PrettyContainer>
  );
}
